import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Database, Link, TrendingUp, Box, LayoutGrid, Network } from 'lucide-react';
import { LocalStarModel } from '../../../../types';
import StarSchemaDiagram from './StarSchemaDiagram';

interface StarSchemaPanelProps {
  starModel: LocalStarModel;
  datasourceName?: string;
}

type ViewMode = 'diagram' | 'list';

export const StarSchemaPanel: React.FC<StarSchemaPanelProps> = ({ starModel, datasourceName }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('diagram');

  return (
    <div className="space-y-6">
      {/* Summary Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-xl p-6"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center">
              <Star className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Star Schema Preview</h3>
              {datasourceName && (
                <p className="text-sm text-gray-400">Auto-generated for: {datasourceName}</p>
              )}
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('diagram')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                viewMode === 'diagram'
                  ? 'bg-indigo-500/30 text-indigo-300 border border-indigo-500/50'
                  : 'bg-slate-700/30 text-gray-400 hover:bg-slate-700/50'
              }`}
            >
              <Network className="w-4 h-4" />
              <span className="text-sm">Diagram</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                viewMode === 'list'
                  ? 'bg-indigo-500/30 text-indigo-300 border border-indigo-500/50'
                  : 'bg-slate-700/30 text-gray-400 hover:bg-slate-700/50'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="text-sm">List</span>
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-800/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-gray-400">Fact Tables</span>
            </div>
            <p className="text-2xl font-bold text-white">{starModel.summary.totalFacts}</p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Box className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-gray-400">Dimensions</span>
            </div>
            <p className="text-2xl font-bold text-white">{starModel.summary.totalDimensions}</p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-sky-400" />
              <span className="text-xs text-gray-400">Measures</span>
            </div>
            <p className="text-2xl font-bold text-white">{starModel.summary.totalMeasures}</p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Link className="w-4 h-4 text-orange-400" />
              <span className="text-xs text-gray-400">Relationships</span>
            </div>
            <p className="text-2xl font-bold text-white">{starModel.summary.totalRelationships}</p>
          </div>
        </div>
      </motion.div>

      {/* Interactive Diagram View */}
      {viewMode === 'diagram' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <StarSchemaDiagram starModel={starModel} />
        </motion.div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {/* Fact Tables */}
      {starModel.facts.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-4"
        >
          <h4 className="text-md font-semibold text-white flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-400" />
            Fact Tables
          </h4>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {starModel.facts.map((fact, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                className="bg-slate-800/60 border border-blue-500/30 rounded-lg p-4 hover:border-blue-500/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h5 className="font-semibold text-white">{fact.name}</h5>
                    <p className="text-xs text-gray-400">Source: {fact.sourceTable}</p>
                  </div>
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">
                    Fact
                  </span>
                </div>

                {/* Grain */}
                {fact.grain.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-400 mb-1">Grain (Keys):</p>
                    <div className="flex flex-wrap gap-1">
                      {fact.grain.map((key, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-slate-700/50 text-gray-300 text-xs rounded"
                        >
                          {key}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Measures */}
                <div>
                  <p className="text-xs text-gray-400 mb-2">
                    Measures ({fact.measures.length}):
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {fact.measures.map((measure, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between px-2 py-1 bg-slate-700/30 rounded text-xs"
                      >
                        <span className="text-gray-300">{measure.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">{measure.type}</span>
                          <span className="px-1.5 py-0.5 bg-sky-500/20 text-sky-400 rounded">
                            {measure.aggregation}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Dimension Tables */}
      {starModel.dimensions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          <h4 className="text-md font-semibold text-white flex items-center gap-2">
            <Box className="w-5 h-5 text-purple-400" />
            Dimension Tables
          </h4>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {starModel.dimensions.map((dim, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 + index * 0.05 }}
                className="bg-slate-800/60 border border-purple-500/30 rounded-lg p-4 hover:border-purple-500/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h5 className="font-semibold text-white">{dim.name}</h5>
                    <p className="text-xs text-gray-400">Source: {dim.sourceTable}</p>
                  </div>
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded">
                    Dimension
                  </span>
                </div>

                {/* Attributes */}
                <div>
                  <p className="text-xs text-gray-400 mb-2">
                    Attributes ({dim.attributes.length}):
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {dim.attributes.map((attr, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between px-2 py-1 bg-slate-700/30 rounded text-xs"
                      >
                        <span className="text-gray-300">{attr.name}</span>
                        <span className="text-gray-500">{attr.type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Relationships */}
      {starModel.relationships.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          <h4 className="text-md font-semibold text-white flex items-center gap-2">
            <Link className="w-5 h-5 text-orange-400" />
            Relationships
          </h4>

          <div className="bg-slate-800/60 border border-orange-500/30 rounded-lg overflow-hidden">
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-700/50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-300">
                      Fact Table
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-300">
                      Dimension
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-300">
                      Fact Key
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-300">
                      Dimension Key
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-300">
                      Cardinality
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {starModel.relationships.map((rel, index) => (
                    <tr
                      key={index}
                      className="border-t border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="px-4 py-2 text-sm text-white">{rel.fact}</td>
                      <td className="px-4 py-2 text-sm text-white">{rel.dimension}</td>
                      <td className="px-4 py-2 text-sm text-gray-400 font-mono text-xs">
                        {rel.factKey}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-400 font-mono text-xs">
                        {rel.dimensionKey}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded">
                          {rel.cardinality}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}

      {/* Empty State */}
      {starModel.facts.length === 0 && starModel.dimensions.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12 text-gray-400"
        >
          <Star className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>No star schema available</p>
          <p className="text-sm mt-2">Analyze the datasource to generate the schema</p>
        </motion.div>
      )}
        </>
      )}
    </div>
  );
};
